import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { get, post, put } from "aws-amplify/api";
import '../../common/Detail.css';
import RecordHistory from "containers/common/RecordHistory";
import EntityPropertyList from "containers/entity/page/EntityPropertyList";
import EntityPropertySaleList from "containers/entity/page/EntityPropertySaleList";
import EntityLeasingAgentList from "containers/entity/page/EntityLeasingAgentList";
import EntityLiftOperatorList from "containers/entity/page/EntityLiftOperatorList";
import EntityPropertySaleSheetList from "containers/entity/page/EntityPropertySaleSheetList";
import EntityPageContactList from "containers/entity/page/EntityPageContactList";
import EntityPageGeneralInfo from "containers/entity/page/EntityPageGeneralInfo";
import EntityPageFindAndReplace from "containers/entity/page/EntityPageFindAndReplace";
import { PacmanLoader } from 'react-spinners';
import { useParams, useNavigate } from "react-router-dom";
import { Form, Row, Col, Card, Tabs, Tab, Button, Alert, Nav, Modal } from "react-bootstrap";
import { createElementIdFactory } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import "containers/entity/EntitySearch.css";

const initialEntity = {
    entity_id: "",
    origin_entity_id: null,
    name: null,
    telephone: null,
    fax: null,
    notes: null,
    st_care_of: null,
    st_premises_xx: null,
    st_building_name_xx: null,
    st_street_number_xx: null,
    st_street_name_xx: null,
    st_address_line_1: null,
    st_address_line_2: null,
    st_suburb: null,
    st_postcode: null,
    st_state: null,
    st_country: null,
    pa_care_of: null,
    pa_po_box: null,
    pa_suburb: null,
    pa_postcode: null,
    pa_state: null,
    pa_country: null,
    internet: null,
    email: null,
    inactive: false,
    entity_type_code: null,
    entity_type: null,
    search_state: null,
    created_date: null,
    created_by: null,
    last_edited_date: null,
    last_edited_by: null
}

function EntityPageDetail({
    isAuthenticated,
    user,
    countryOptions,
    countryCodeOptions,
    suburbOptions,
    salutationOptions,
    entityTypeOptions,
    positionOptions,
    validateRelatedRecord,
}) {

    const params = useParams();
    const navigate = useNavigate();
    const createId = useMemo(() => createElementIdFactory("entity-detail"), []);
    const [entity, setEntity] = useState(initialEntity);
    const {
        created_by,
        created_date,
        last_edited_by,
        last_edited_date,
    } = entity;
    const [ui, setUi] = useState({
        isLoading: params.id !== 'new',
        isSaving: false,
        isIndexLoading: false,
        isDeleting: false,
        showAlert: false,
        errorMessage: ""
    });
    const [collections, setCollections] = useState({
        suburbOptions: [],
        countryOptions: countryOptions,
        countryCodeOptions: countryCodeOptions,
        entityTypeOptions: entityTypeOptions,
        positionOptions: positionOptions,
        salutationOptions: salutationOptions,
        contacts: [],
        leasingAgents: [],
        leasingAgentOptions: [],
        leasingAgentContactOptions: [],
        liftOperators: [],
        entitySales: [],
        entitySaleSheets: [],
        entityProperties: [],
    });
    const [entitySalesCount, setEntitySalesCount] = useState(0);
    const [entitySaleSheetsCount, setEntitySaleSheetsCount] = useState(0);
    const [lastEditedField, setLastEditedField] = useState(null);
    const [showEntityFindAndReplace, setShowEntityFindAndReplace] = useState(false);
    const suppressNextAutoSaveRef = useRef(false);
    const editVersionRef = useRef(0);
    const saveRequestIdRef = useRef(0);
    const activeSaveCountRef = useRef(0);

    const currentUserDisplay = useMemo(() => {
        if (typeof user === "string") {
            return user;
        }

        if (!user || typeof user !== "object") {
            return "";
        }

        return user.name
            || user.username
            || user.user_name
            || user.email
            || user.preferred_username
            || user?.attributes?.name
            || user?.attributes?.preferred_username
            || user?.attributes?.email
            || "";
    }, [user]);

    const duplicateEntityCodeFallbackMessage = "Entity Code already exists. No further edits to this Entity record will be saved until the Entity Code is restored to a unique value. To abandon the Entity Code edit and restore the previous value, press F5.";

    const isNewEntityRecord = useCallback((candidate) => {
        const entityId = candidate?.entity_id;
        return !entityId || entityId === "new" || String(entityId).trim() === "";
    }, []);

    const getMissingRequiredFieldMessage = useCallback((candidate) => {
        if (!candidate?.origin_entity_id || String(candidate.origin_entity_id).trim() === "") {
            return "You must enter an entity code.";
        }

        if (!candidate?.name || String(candidate.name).trim() === "") {
            return "You must enter an entity name.";
        }

        if (!candidate?.st_state || String(candidate.st_state).trim() === "") {
            return "You must select a state.";
        }

        return "";
    }, []);

    const clearEditedFieldAfterError = useCallback((fieldName) => {
        if (!fieldName) {
            setEntity(prev => ({ ...prev, modified: false }));
            return;
        }

        setEntity(prev => {
            const currentValue = prev[fieldName];
            const clearedValue = typeof currentValue === "boolean" ? false : "";
            const updated = {
                ...prev,
                [fieldName]: clearedValue,
                modified: false,
            };

            if (fieldName === "st_state") {
                updated.search_state = "";
            }

            if (fieldName === "st_suburb") {
                updated.st_state = "";
                updated.st_postcode = "";
                updated.search_state = "";
            }

            if (fieldName === "pa_suburb") {
                updated.pa_state = "";
                updated.pa_postcode = "";
            }

            return updated;
        });
    }, []);

    const handleEntitySaveError = useCallback((result, fallbackMessage, sourceField = null) => {
        const errorMessage = result?.error_message || fallbackMessage;
        const fieldToClear = sourceField || lastEditedField;

        window.alert(errorMessage);
        setUi(prev => ({ ...prev, errorMessage, showAlert: false }));

        // Avoid repeated save attempts caused by blur chaining after the popup closes.
        suppressNextAutoSaveRef.current = true;
        clearEditedFieldAfterError(fieldToClear);
    }, [clearEditedFieldAfterError, lastEditedField]);

    const normalizeCollection = useCallback((payload) => {
        if (Array.isArray(payload)) return payload;
        if (!payload || typeof payload !== "object") return [];

        const keysToCheck = ["items", "data", "results", "rows", "records", "contacts", "entitySales", "entitySaleSheets", "entityProperties", "entityLeasingAgents", "leasingAgents", "entityLiftOperators", "liftOperators"];
        for (const key of keysToCheck) {
            if (Array.isArray(payload[key])) {
                return payload[key];
            }
        }

        return [];
    }, []);

    // --- Data Fetching and Update Functions ---
    const getEntityDetail = useCallback(async () => {
        const response = await get({
            apiName: "colin",
            path: `/entities/${params.id}`,
        }).response;
        try {
            const payload = await response.body.json();
            const source = payload?.entity || payload?.data || payload || {};
            const auditSource = source?.audit || source?.record_history || payload?.audit || payload?.record_history || {};
            const mappedName = source?.name || source?.entity_name || payload?.name || "";

            const pickFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

            const toDisplayUser = (value) => {
                if (value == null) {
                    return value;
                }

                if (typeof value === "object") {
                    return value.name || value.username || value.user_name || value.email || "";
                }

                return value;
            };

            const createdDate = pickFirstDefined(
                source?.created_date,
                source?.createdDate,
                source?.created_datetime,
                source?.createdDateTime,
                auditSource?.created_date,
                auditSource?.createdDate,
                payload?.created_date,
                payload?.createdDate,
            );

            const createdBy = toDisplayUser(
                pickFirstDefined(
                    source?.created_by,
                    source?.createdBy,
                    source?.created_user,
                    source?.createdUser,
                    auditSource?.created_by,
                    auditSource?.createdBy,
                    payload?.created_by,
                    payload?.createdBy,
                )
            );

            const lastEditedDate = pickFirstDefined(
                source?.last_edited_date,
                source?.lastEditedDate,
                source?.updated_date,
                source?.updatedDate,
                source?.modified_date,
                source?.modifiedDate,
                auditSource?.last_edited_date,
                auditSource?.lastEditedDate,
                auditSource?.updated_date,
                auditSource?.updatedDate,
                payload?.last_edited_date,
                payload?.lastEditedDate,
            );

            const lastEditedBy = toDisplayUser(
                pickFirstDefined(
                    source?.last_edited_by,
                    source?.lastEditedBy,
                    source?.updated_by,
                    source?.updatedBy,
                    source?.modified_by,
                    source?.modifiedBy,
                    auditSource?.last_edited_by,
                    auditSource?.lastEditedBy,
                    auditSource?.updated_by,
                    auditSource?.updatedBy,
                    payload?.last_edited_by,
                    payload?.lastEditedBy,
                )
            );

            const mappedEntity = {
                ...source,
                name: mappedName,
                created_date: createdDate,
                created_by: createdBy,
                last_edited_date: lastEditedDate,
                last_edited_by: lastEditedBy,
            };
            return mappedEntity;
        } catch (error) {
            alert(error + ". GET ENTITY DETAIL Please refresh the page (F5) to try again.");
        }
    }, [params.id]);

    const getSuburbs = useCallback(async () => {
        try {
            const response = await get({
                apiName: "colin",
                path: `/suburbs`,
            }).response;

            return await response.body.json();

        } catch (error) {
            console.error("Error fetching suburbs:", error);
            return [];
        }
    }, []);

    const saveEntityAPI = async (data) => {
        const entityId = data?.entity_id;
        const normalizedEntityId = entityId === null || entityId === undefined ? "" : String(entityId).trim();
        const isNew = normalizedEntityId === "" || normalizedEntityId === "new";
        const normalizedPayload = {
            ...data,
            last_edited_by: data?.last_edited_by || currentUserDisplay,
        };

        if (isNew && !normalizedPayload.created_by) {
            normalizedPayload.created_by = currentUserDisplay;
        }

        const path = isNew ? "/entities/new" : `/entities/${data.entity_id}`;
        const method = isNew ? post : put;

        try {
            const response = await method({
                apiName: "colin",
                path: path,
                options: { body: normalizedPayload }
            }).response;

            const result = await response.body.json();

            // Map 'id' from API response to 'entity_id' for app consistency
            if (result?.id && !result?.entity_id) {
                result.entity_id = result.id;
            }

            return result;
        } catch (error) {
            console.error("API Save Error:", error);
            throw error;
        }
    };

    const saveRecord = async (record, dataSet, idKey) => {
        const isNew = !record[idKey] || String(record[idKey]).includes('temp') || record.new_record === true;

        try {
            const method = isNew ? post : put;

            const endpoint = String(dataSet || "").toLowerCase();
            const path = isNew
                ? `/entities/${entity.entity_id}/${endpoint}/new`
                : `/entities/${entity.entity_id}/${endpoint}/${record[idKey]}`;

            const { body } = await method({
                apiName: "colin",
                path: path,
                options: { body: record }
            }).response;

            const result = await body.json();

            const savedItem = {
                ...record,
                ...result,
                [idKey]: result.id || result.subentity_id || record[idKey],
                new_record: false,
                modified: false
            };

            setCollections(prev => {
                const currentList = prev[dataSet] || [];

                return {
                    ...prev,
                    [dataSet]: currentList.map(item =>
                        item[idKey] === record[idKey] ? savedItem : item
                    )
                };
            });

        } catch (error) {
            console.error("Save failed:", error);
        }
    };

    const getEntityContacts = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/entities/${params.id}/contacts`,
            options: {
                queryParams: filter,
            },
        }).response;

        return normalizeCollection(await response.body.json());
    }, [normalizeCollection, params.id]);

    const getEntitySales = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/entitysales`,
            options: {
                queryParams: filter,
            },
        }).response;

        const entitySales = normalizeCollection(await response.body.json());
        entitySales.sort((a, b) => (a.property_key > b.property_key ? 1 : b.property_key > a.property_key ? -1 : 0));
        entitySales.sort((a, b) => (b.last_sale ? 1 : a.last_sale ? -1 : 0));
        return entitySales;
    }, [normalizeCollection]);

    const getEntityLeasingAgents = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/entityleasingagents`,
            options: {
                queryParams: filter,
            },
        }).response;

        const entityLeasingAgents = normalizeCollection(await response.body.json());
        entityLeasingAgents.sort((a, b) => (a.property_key > b.property_key ? 1 : b.property_key > a.property_key ? -1 : 0));
        return entityLeasingAgents;
    }, [normalizeCollection]);

    const getEntityLiftOperators = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/entityliftoperators`,
            options: {
                queryParams: filter,
            },
        }).response;

        const entityLiftOperators = normalizeCollection(await response.body.json());
        entityLiftOperators.sort((a, b) => (a.property_key > b.property_key ? 1 : b.property_key > a.property_key ? -1 : 0));
        return entityLiftOperators;
    }, [normalizeCollection]);

    const getAgentsLeasing = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/entities/dropdown`,
            options: {
                queryParams: filter,
            },
        }).response;

        const options = normalizeCollection(await response.body.json());
        setCollections(prev => ({ ...prev, leasingAgentOptions: options }));
        return options;
    }, [normalizeCollection]);

    const getLeasingAgentContacts = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/entitycontacts/dropdown`,
            options: {
                queryParams: filter,
            },
        }).response;

        const options = normalizeCollection(await response.body.json());
        setCollections(prev => ({ ...prev, leasingAgentContactOptions: options }));
        return options;
    }, [normalizeCollection]);

    const getEntitySalesSheets = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/entitysalesheets`,
            options: {
                queryParams: filter,
            },
        }).response;

        const entitySaleSheets = normalizeCollection(await response.body.json());
        entitySaleSheets.sort((a, b) => (a.property_key > b.property_key ? 1 : b.property_key > a.property_key ? -1 : 0));
        entitySaleSheets.sort((a, b) => (b.current ? 1 : a.current ? -1 : 0));
        return entitySaleSheets;
    }, [normalizeCollection]);

    const getEntityProperties = useCallback(async (filter) => {
        const response = await get({
            apiName: "colin",
            path: `/properties`,
            options: {
                queryParams: filter,
            },
        }).response;

        const entityProperties = normalizeCollection(await response.body.json());
        entityProperties.sort((a, b) => (a.property_key > b.property_key ? 1 : b.property_key > a.property_key ? -1 : 0));
        return entityProperties;
    }, [normalizeCollection]);

    const refreshDataSet = useCallback(async (dataSet) => {
        if (params.id === "new") {
            return;
        }

        try {
            if (dataSet === "contacts") {
                const contacts = await getEntityContacts({ entityid: params.id });
                setCollections(prev => ({ ...prev, contacts }));
                return;
            }

            if (dataSet === "entitySales") {
                const entitySales = await getEntitySales({ entityid: params.id });
                setCollections(prev => ({ ...prev, entitySales }));
                setEntitySalesCount(entitySales[0]?.sales_count || entitySales.length || 0);
                return;
            }

            if (dataSet === "leasingAgents") {
                const leasingAgents = await getEntityLeasingAgents({ entityid: params.id });
                setCollections(prev => ({ ...prev, leasingAgents }));
                return;
            }

            if (dataSet === "liftOperators") {
                const liftOperators = await getEntityLiftOperators({ entityid: params.id });
                setCollections(prev => ({ ...prev, liftOperators }));
                return;
            }

            if (dataSet === "entitySaleSheets") {
                const entitySaleSheets = await getEntitySalesSheets({ entityid: params.id });
                setCollections(prev => ({ ...prev, entitySaleSheets }));
                setEntitySaleSheetsCount(entitySaleSheets[0]?.sales_sheets_count || entitySaleSheets.length || 0);
                return;
            }

            if (dataSet === "entityProperties") {
                const entityProperties = await getEntityProperties({ entityid: params.id });
                setCollections(prev => ({ ...prev, entityProperties }));
            }
        } catch (error) {
            console.error(`Failed refreshing dataset ${dataSet}:`, error);
        }
    }, [getEntityContacts, getEntityLeasingAgents, getEntityLiftOperators, getEntityProperties, getEntitySales, getEntitySalesSheets, params.id]);

    const ensureSuburbOptionsLoaded = useCallback(async () => {
        if ((collections.suburbOptions || []).length > 0) {
            return;
        }

        try {
            const loadedSuburbs = await getSuburbs();
            setCollections(prev => ({
                ...prev,
                suburbOptions: Array.isArray(loadedSuburbs) ? loadedSuburbs : []
            }));
        } catch (error) {
            console.error("Failed to load suburbs on demand:", error);
        }
    }, [collections.suburbOptions, getSuburbs]);

    const deleteRecord = async (dataSet, record) => {
        const archivedRecord = {
            ...record,
            archived_date: new Date().toISOString(),
            last_edited_by: currentUserDisplay,
        };

        return saveRecord(archivedRecord, dataSet, "entity_contact_id");
    };

    // --- Effect Hook --
    const loadAllEntityData = useCallback(async () => {
        setUi(prev => ({ ...prev, isLoading: true }));

        try {
            const [
                localEntity,
                contacts,
                leasingAgents,
                liftOperators,
                entitySales,
                entitySaleSheets,
                entityProperties,
            ] = await Promise.all([
                params.id !== "new" ? getEntityDetail() : Promise.resolve(initialEntity),
                params.id !== "new" ? getEntityContacts({ entityid: params.id }) : Promise.resolve([]),
                params.id !== "new" ? getEntityLeasingAgents({ entityid: params.id }) : Promise.resolve([]),
                params.id !== "new" ? getEntityLiftOperators({ entityid: params.id }) : Promise.resolve([]),
                params.id !== "new" ? getEntitySales({ entityid: params.id }) : Promise.resolve([]),
                params.id !== "new" ? getEntitySalesSheets({ entityid: params.id }) : Promise.resolve([]),
                params.id !== "new" ? getEntityProperties({ entityid: params.id }) : Promise.resolve([]),
            ]);

            setEntity(prev => ({ ...initialEntity, ...localEntity }));
            setCollections(prev => ({
                ...prev,
                contacts,
                leasingAgents,
                liftOperators,
                entitySales,
                entitySaleSheets,
                entityProperties,
            }));
            setEntitySalesCount(entitySales[0]?.sales_count || entitySales.length || 0);
            setEntitySaleSheetsCount(entitySaleSheets[0]?.sales_sheets_count || entitySaleSheets.length || 0);

        } catch (error) {
            setUi(prev => ({ ...prev, errorMessage: "Failed to load entity data", showAlert: true }));
        } finally {
            setUi(prev => ({ ...prev, isLoading: false }));
        }
    }, [params.id, getEntityContacts, getEntityDetail, getEntityLeasingAgents, getEntityLiftOperators, getEntityProperties, getEntitySales, getEntitySalesSheets]);

    useEffect(() => {
        if (isAuthenticated) {
            loadAllEntityData();
        }
    }, [isAuthenticated, loadAllEntityData]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        if (params.id === "new") {
            document.title = "New entity";
            return;
        }

        document.title = `Entity ${entity?.name || ""}`.trim();
    }, [entity?.name, isAuthenticated, params.id]);

    // Navigate to the new entity after successful creation
    useEffect(() => {
        if (params.id === "new" && entity.entity_id && entity.entity_id !== "new" && String(entity.entity_id).trim() !== "") {
            navigate(`/entities/${entity.entity_id}`, { replace: true });
        }
    }, [entity.entity_id, params.id, navigate]);



    // --- Input Change Handlers ---
    const handleChange = (e, customName = null) => {
        let name, value;
        if (!e || !e.target) {
            name = customName;
            value = e;
        } else {
            const { name: targetName, value: targetValue, type, checked } = e.target;
            name = targetName;
            value = type === 'checkbox' ? checked : targetValue;
        }

        if (name === "suburb" && value?.value) {
            setEntity(prev => ({
                ...prev,
                suburb: value.value,
                state: value.state || prev.state,
                post_code: value.postcode || prev.post_code,
                modified: true,
            }));
        } else {
            setEntity(prev => ({
                ...prev,
                [name]: value,
                modified: true,
            }));
        }

        editVersionRef.current += 1;

        setLastEditedField(name);
        setUi(prev => ({ ...prev, showAlert: false }));
    };

    const handleImmediateSave = async (updatedData, sourceField = null, options = {}) => {
        const { forceSave = false } = options;
        const requestId = ++saveRequestIdRef.current;
        const editVersionAtRequest = editVersionRef.current;

        if (suppressNextAutoSaveRef.current && !forceSave) {
            suppressNextAutoSaveRef.current = false;
            return;
        }

        if (sourceField) {
            setLastEditedField(sourceField);

            // Skip unnecessary network saves when the value did not actually change.
            if (!forceSave && (updatedData?.[sourceField] ?? "") === (entity?.[sourceField] ?? "")) {
                return;
            }
        }

        if (isNewEntityRecord(updatedData)) {
            const validationMessage = getMissingRequiredFieldMessage(updatedData);
            if (validationMessage) {
                setUi(prev => ({ ...prev, errorMessage: validationMessage, showAlert: true }));
                return;
            }
        }

        const payloadToSave = {
            ...updatedData,
            last_edited_by: currentUserDisplay,
        };

        try {
            // Prevent a concurrent handleBlur save (triggered by blur-then-click) from
            // racing with this call and double-POSTing to /entities/new.
            if (isNewEntityRecord(payloadToSave) && activeSaveCountRef.current > 0) {
                return;
            }
            activeSaveCountRef.current += 1;
            setUi(prev => ({ ...prev, isSaving: true }));
            setEntity(payloadToSave);
            const result = await saveEntityAPI(payloadToSave);

            if (result?.result === false) {
                handleEntitySaveError(result, duplicateEntityCodeFallbackMessage, sourceField);
                return;
            }

            setEntity(prev => {
                const hasNewerEdits = editVersionRef.current > editVersionAtRequest;
                const isLatestRequest = requestId === saveRequestIdRef.current;

                if (hasNewerEdits || !isLatestRequest) {
                    const nextEntityId = result?.entity_id || result?.id;
                    if ((prev.entity_id === "new" || !prev.entity_id) && nextEntityId) {
                        return { ...prev, entity_id: nextEntityId };
                    }
                    return prev;
                }

                return { ...prev, ...result, modified: false };
            });
        } catch (e) {
            if (requestId === saveRequestIdRef.current) {
                setUi(prev => ({ ...prev, errorMessage: "Save failed", showAlert: true }));
            }
        } finally {
            activeSaveCountRef.current = Math.max(0, activeSaveCountRef.current - 1);
            setUi(prev => ({ ...prev, isSaving: activeSaveCountRef.current > 0 }));
        }
    };

    const handleBlur = async (payload = null) => {
        const requestId = ++saveRequestIdRef.current;
        const editVersionAtRequest = editVersionRef.current;

        if (suppressNextAutoSaveRef.current) {
            suppressNextAutoSaveRef.current = false;
            return;
        }

        setUi(prevUi => ({ ...prevUi, showAlert: false }));

        const explicitFieldName = payload?.fieldName || payload?.target?.name || payload?.target?.id || null;
        const explicitUpdatedEntity = payload?.updatedEntity || null;

        if (explicitFieldName) {
            setLastEditedField(explicitFieldName);
        }

        if (!explicitUpdatedEntity && !entity.modified) return;

        const updatedEntity = {
            ...(explicitUpdatedEntity || entity),
            last_edited_by: currentUserDisplay,
        };

        if (explicitUpdatedEntity) {
            setEntity(updatedEntity);
        }

        let isValid = true;
        let validationMessage = "";

        if (isNewEntityRecord(updatedEntity)) {
            validationMessage = getMissingRequiredFieldMessage(updatedEntity);
            isValid = !validationMessage;
        }

        if (isValid && typeof validateRelatedRecord === "function") {
            const validationResult = validateRelatedRecord(updatedEntity, "entity");

            if (Array.isArray(validationResult)) {
                isValid = validationResult[0] !== false;
                validationMessage = validationResult[1] || "";
            } else if (typeof validationResult === "boolean") {
                isValid = validationResult;
            }
        }

        if (!isValid) {
            setUi(prevUi => ({
                ...prevUi,
                errorMessage: validationMessage,
                showAlert: true
            }));
            return;
        }

        try {
            activeSaveCountRef.current += 1;
            setUi(prevUi => ({ ...prevUi, isSaving: true }));

            const isCreatingNew = isNewEntityRecord(updatedEntity);
            const result = await saveEntityAPI(updatedEntity);

            if (isCreatingNew && result?.result === false) {
                handleEntitySaveError(result, duplicateEntityCodeFallbackMessage, explicitFieldName);
                setUi(prevUi => ({ ...prevUi, isSaving: false }));
                return;
            }

            setEntity(prev => {
                const hasNewerEdits = editVersionRef.current > editVersionAtRequest;
                const isLatestRequest = requestId === saveRequestIdRef.current;

                if (hasNewerEdits || !isLatestRequest) {
                    const nextEntityId = result?.entity_id || result?.id;
                    if ((prev.entity_id === "new" || !prev.entity_id) && nextEntityId) {
                        return { ...prev, entity_id: nextEntityId };
                    }
                    return prev;
                }

                return { ...updatedEntity, ...result, modified: false };
            });

        } catch (e) {
            if (requestId === saveRequestIdRef.current) {
                setUi(prevUi => ({
                    ...prevUi,
                    errorMessage: "Save failed: " + e.message,
                    showAlert: true
                }));
                setEntity(prev => ({ ...prev, modified: true }));
            }
        } finally {
            activeSaveCountRef.current = Math.max(0, activeSaveCountRef.current - 1);
            setUi(prevUi => ({ ...prevUi, isSaving: activeSaveCountRef.current > 0 }));
        }
    };

    const updateTableState = (indexOrNewData, columnIdOrDataSet, value, dataSet) => {
        if (Array.isArray(indexOrNewData)) {
            const targetKey = columnIdOrDataSet;

            setCollections(prev => {
                return {
                    ...prev,
                    [targetKey]: indexOrNewData
                };
            });
        }
        else {
            const rowIndex = indexOrNewData;
            const colId = columnIdOrDataSet;
            const target = dataSet;

            if (!target || !collections[target]) {
                console.error("DataSet missing or invalid:", target);
                return;
            }

            setCollections(prev => {
                const currentList = [...(prev[target] || [])];
                if (!currentList[rowIndex]) return prev;

                currentList[rowIndex] = {
                    ...currentList[rowIndex],
                    [colId]: value,
                    modified: true
                };

                return {
                    ...prev,
                    [target]: currentList
                };
            });
        }
    };

    const updateContactsTableState = (indexOrNewData, columnIdOrDataSet, value) => {
        if (Array.isArray(indexOrNewData)) {
            updateTableState(indexOrNewData, "contacts");
            return;
        }

        updateTableState(indexOrNewData, columnIdOrDataSet, value, "contacts");
    };

    const saveContactRecord = async (_dataSet, record) => {
        return saveRecord(record, "contacts", "entity_contact_id");
    };

    const updateLeasingAgentsTableState = (indexOrNewData, columnIdOrDataSet, value) => {
        if (Array.isArray(indexOrNewData)) {
            updateTableState(indexOrNewData, "leasingAgents");
            return;
        }

        updateTableState(indexOrNewData, columnIdOrDataSet, value, "leasingAgents");
    };

    const saveLeasingAgentRecord = async (record, _dataSet, idKey = "leasing_agent_id") => {
        return saveRecord(record, "leasingAgents", idKey);
    };

    const updateLiftOperatorsTableState = (indexOrNewData, columnIdOrDataSet, value) => {
        if (Array.isArray(indexOrNewData)) {
            updateTableState(indexOrNewData, "liftOperators");
            return;
        }

        updateTableState(indexOrNewData, columnIdOrDataSet, value, "liftOperators");
    };

    const saveLiftOperatorRecord = async (record, _dataSet, idKey = "lift_operator_id") => {
        return saveRecord(record, "liftOperators", idKey);
    };

    const updatePropertiesTableState = (newData, _dataSet) => {
        updateTableState(newData, "entityProperties");
    };

    const updateSalesTableState = (newData, _dataSet) => {
        updateTableState(newData, "entitySales");
        setEntitySalesCount(Array.isArray(newData) ? newData.length : 0);
    };

    const updateSaleSheetsTableState = (newData, _dataSet) => {
        updateTableState(newData, "entitySaleSheets");
        setEntitySaleSheetsCount(Array.isArray(newData) ? newData.length : 0);
    };

    const saveEntitySalesRecord = async (record, _dataSet, idKey = "property_sale_id") => {
        return saveRecord(record, "propertySales", idKey);
    };

    const saveEntitySaleSheetsRecord = async (record, _dataSet, idKey = "property_sale_sheet_id") => {
        return saveRecord(record, "propertySaleSheets", idKey);
    };

    const toggleEntityFindAndReplace = () => {
        setShowEntityFindAndReplace(prev => !prev);
    };

    // --- Render Functions ---
    function renderForm(entity) {
        const entityName = entity?.name || "";
        return (
            <div className="EntityForm" id={createId("form", "container")}>
                <Form id={createId("form")}> 
                    <Row id={createId("header", "row")}>
                        <h6>Entity Details - {entityName} | {entity.telephone || ""} | {entity.st_state || ""} {entity.inactive && " ** INACTIVE **"} </h6>
                    </Row>
                    <Row id={createId("actions", "row")} className="entity-detail-actions-row">
                        <Col>
                            <Nav className="justify-content-end " >
                                <Nav.Item  >
                                    <a target="_blank" href="/salutations">
                                        <Button variant="primary" type="button" size="sm">Add/Edit Salutations</Button>{" "}
                                    </a>
                                </Nav.Item>
                                <Nav.Item  >
                                    <a target="_blank" href="/positions">
                                        <Button variant="primary" type="button" size="sm">Add/Edit Positions</Button>{" "}
                                    </a>
                                </Nav.Item>
                                <Nav.Item>
                                    <Button type="button" variant="primary" size="sm" onClick={toggleEntityFindAndReplace}>
                                        Entity Find &amp; Replace
                                    </Button>{" "}
                                </Nav.Item>
                            </Nav>
                        </Col>
                    </Row>

                    <Row id={createId("detail", "row")}>
                        <Col xs={12}>
                        <Tabs defaultActiveKey="entity-detail" id={createId("tabs")} className="justify-content-start entity-detail-tabs">

                            <Tab id={createId("tab", "entity-detail")} eventKey="entity-detail" title="Entity Detail">
                                <Card className="entity-detail-tab-card">
                                    <Card.Header>Entity Details + {entityName}
                                    </Card.Header>
                                    <Card.Body>
                                        <EntityPageGeneralInfo
                                            entity={entity}
                                            collections={collections}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            handleImmediateSave={handleImmediateSave}
                                            ensureSuburbOptionsLoaded={ensureSuburbOptionsLoaded}
                                            setUi={setUi}
                                            saveRecord={saveRecord}
                                            user={user}
                                            updateTableState={updateTableState}
                                        />
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab id={createId("tab", "contacts")} eventKey="contacts" title={`Contacts (${collections.contacts.length})`}>
                                <Card className="entity-detail-tab-card">
                                    <Card.Body>
                                        <EntityPageContactList
                                            isAuthenticated={isAuthenticated}
                                            user={user}
                                            data={collections.contacts}
                                            salutationOptions={collections.salutationOptions}
                                            positionOptions={collections.positionOptions}
                                            updateTableState={updateContactsTableState}
                                            saveRecord={saveContactRecord}
                                            deleteRecord={deleteRecord}
                                            refreshDataSet={refreshDataSet}
                                            setUi={setUi}
                                            validateRelatedRecord={validateRelatedRecord}
                                            entityid={entity.entity_id}
                                        />
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab id={createId("tab", "managing-agents")} eventKey="managing-agents" title={`Managing Agents (${collections.entityProperties.length})`}>
                                <Card className="entity-detail-tab-card">
                                    <Card.Body>
                                        <EntityPropertyList
                                            isAuthenticated={isAuthenticated}
                                            data={collections.entityProperties}
                                            propData={collections.entityProperties}
                                            updateTableState={updatePropertiesTableState}
                                            controlType="table1"
                                        />
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab id={createId("tab", "leasing-agents")} eventKey="leasing-agents" title={`Leasing Agents (${collections.leasingAgents.length})`}>
                                <Card className="entity-detail-tab-card">
                                    <Card.Body>
                                        <EntityLeasingAgentList
                                            isAuthenticated={isAuthenticated}
                                            user={user}
                                            data={collections.leasingAgents}
                                            updateTableState={updateLeasingAgentsTableState}
                                            saveRecord={saveLeasingAgentRecord}
                                            refreshDataSet={() => refreshDataSet("leasingAgents")}
                                            validateRelatedRecord={validateRelatedRecord}
                                            getAgentsLeasing={getAgentsLeasing}
                                            getLeasingAgentContacts={getLeasingAgentContacts}
                                            leasingAgentOptions={collections.leasingAgentOptions}
                                            leasingAgentContactOptions={collections.leasingAgentContactOptions}
                                            propertyid={entity.entity_id}
                                            state={entity.st_state}
                                            controlType="leasingAgentTable"
                                        />
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab id={createId("tab", "lift-operators")} eventKey="lift-operators" title={`Lift Operators (${collections.liftOperators.length})`}>
                                <Card className="entity-detail-tab-card">
                                    <Card.Body>
                                        <EntityLiftOperatorList
                                            isAuthenticated={isAuthenticated}
                                            user={user}
                                            data={collections.liftOperators}
                                            updateTableState={updateLiftOperatorsTableState}
                                            saveRecord={saveLiftOperatorRecord}
                                            refreshDataSet={() => refreshDataSet("liftOperators")}
                                            liftOperatorOptions={[]}
                                            controlType="liftOperatorTable"
                                            validateRelatedRecord={validateRelatedRecord}
                                            propertyid={entity.entity_id}
                                        />
                                    </Card.Body>
                                </Card>
                            </Tab>

                            {/* <Tab eventKey={6} title={"Sales (" + entitySalesCount + ")"}> */}
                            <Tab id={createId("tab", "sales")} eventKey="sales" title={`Sales (${entitySalesCount})`}>
                                <Card className="entity-detail-tab-card" style={{ width: '100%' }}>
                                    <Card.Body>
                                        <EntityPropertySaleList
                                            isAuthenticated={isAuthenticated}
                                            user={user}
                                            data={collections.entitySales}
                                            updateTableState={updateSalesTableState}
                                            controlType="table4"
                                            tableProfile="entity-sales"
                                            saveRecord={saveEntitySalesRecord}
                                            refreshDataSet={() => refreshDataSet("entitySales")}
                                            validateRelatedRecord={validateRelatedRecord}
                                        />
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab id={createId("tab", "sale-sheets")} eventKey="sale-sheets" title={`Sale Sheets (${entitySaleSheetsCount})`}>
                                <Card className="entity-detail-tab-card">
                                    <Card.Body>
                                        <EntityPropertySaleSheetList
                                            user={user}
                                            isAuthenticated={isAuthenticated}
                                            data={collections.entitySaleSheets}
                                            updateTableState={updateSaleSheetsTableState}
                                            saveRecord={saveEntitySaleSheetsRecord}
                                            deleteRecord={deleteRecord}
                                            refreshDataSet={() => refreshDataSet("entitySaleSheets")}
                                            validateRelatedRecord={validateRelatedRecord}
                                            controlType="table3"
                                            tableProfile="entity-salesheets"
                                        />
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab id={createId("tab", "record-history")} eventKey="record-history" title="Record History">
                                <RecordHistory
                                    created_date={created_date}
                                    created_by={created_by}
                                    last_edited_date={last_edited_date}
                                    last_edited_by={last_edited_by}
                                />
                            </Tab>
                        </Tabs>
                        </Col>
                    </Row>
                </Form>
            </div>
        )
    }

    function renderComponent() {
        return (
            <div className="Entity" id={createId("content") }>
                {showEntityFindAndReplace && (
                    <Modal show={showEntityFindAndReplace} onHide={toggleEntityFindAndReplace} dialogClassName="entity-search-modal-90w">
                        <Modal.Header closeButton>
                            <Modal.Title>Find &amp; Replace Entity</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <EntityPageFindAndReplace
                                isAuthenticated={isAuthenticated}
                                user={user}
                                onClose={toggleEntityFindAndReplace}
                            />
                        </Modal.Body>
                    </Modal>
                )}

                <Modal
                    show={ui.showAlert}
                    onHide={() => setUi(prev => ({ ...prev, showAlert: false }))}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Uh Oh!</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="danger" className="mb-0">{ui.errorMessage}</Alert>
                    </Modal.Body>
                </Modal>

                <Row className="show-Container" id={createId("loading", "row")}>
                    <Col sm={12} md={12} lg={12}>
                        {ui.isLoading && (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color={"#e03c31"}
                                        loading={ui.isLoading}
                                    />
                                </div>
                            </div>
                        )}

                        {!ui.isLoading && entity
                            ? renderForm(entity)
                            : null
                        }
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <div className="EntityPageDetail" id={createId("page") }>
            {isAuthenticated ? renderComponent() : null}
        </div>
    );
}

export default EntityPageDetail;